<template>
  <div class="mt-8 px-6">
    <h1 class="text-xl font-semibold leading-tight text-gray-700 my-3">
      Coupon & Discount Management
    </h1>
  </div>
  <div class="p-6 bg-white rounded-md shadow-md">
    <!-- Form for creating/updating coupon -->
    <div class="mb-6">
      <h2 class="text-lg font-semibold text-gray-800 mb-4">
        {{ editingId ? "Edit Coupon" : "Create New Coupon" }}
      </h2>
      <form @submit.prevent="saveCoupon">
        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3 mb-4">
          <!-- Coupon Code -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Coupon Code *
            </label>
            <input
              v-model="form.code"
              type="text"
              placeholder="e.g., SUMMER20"
              :disabled="editingId"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none disabled:bg-gray-100"
            />
            <span v-if="errors.code" class="text-red-500 text-xs mt-1">{{ errors.code }}</span>
          </div>

          <!-- Discount Percentage -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Discount % *
            </label>
            <input
              v-model.number="form.discountPercent"
              type="number"
              min="0"
              max="100"
              placeholder="e.g., 20"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
            />
            <span v-if="errors.discountPercent" class="text-red-500 text-xs mt-1">{{ errors.discountPercent }}</span>
          </div>

          <!-- Minimum Amount -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Minimum Order Amount
            </label>
            <input
              v-model.number="form.minAmount"
              type="number"
              min="0"
              step="0.01"
              placeholder="e.g., 50.00"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
            />
            <span v-if="errors.minAmount" class="text-red-500 text-xs mt-1">{{ errors.minAmount }}</span>
          </div>

          <!-- Expiry Date -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Expiry Date *
            </label>
            <input
              v-model="form.expiryDate"
              type="datetime-local"
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
            />
            <span v-if="errors.expiryDate" class="text-red-500 text-xs mt-1">{{ errors.expiryDate }}</span>
          </div>

          <!-- Active Status -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <div class="flex items-center">
              <input
                v-model="form.active"
                type="checkbox"
                id="active"
                class="w-4 h-4 text-primary rounded"
              />
              <label for="active" class="ml-2 text-sm text-gray-700">Active</label>
            </div>
          </div>
        </div>

        <!-- Description -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            v-model="form.description"
            placeholder="Optional: Describe the coupon purpose"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary focus:outline-none"
          ></textarea>
        </div>

        <!-- Error Alert -->
        <div v-if="formError" class="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          {{ formError }}
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end gap-2">
          <button
            v-if="editingId"
            @click="resetForm"
            type="button"
            class="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="isSubmitting"
            class="px-6 py-2 text-white bg-primary rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
          >
            <fa-icon v-if="isSubmitting" icon="spinner" :spin="true" class="mr-2" />
            {{ editingId ? "Update Coupon" : "Create Coupon" }}
          </button>
        </div>
      </form>
    </div>

    <hr class="my-6" />

    <!-- Coupons List Table -->
    <div class="mt-6">
      <h2 class="text-lg font-semibold text-gray-800 mb-4">
        Active Coupons ({{ coupons.length }})
      </h2>
      <div class="overflow-x-auto rounded-lg shadow">
        <table class="w-full text-left text-sm">
          <thead class="bg-gray-50 border-b">
            <tr>
              <th class="px-6 py-3 font-semibold text-gray-700">Code</th>
              <th class="px-6 py-3 font-semibold text-gray-700">Discount</th>
              <th class="px-6 py-3 font-semibold text-gray-700">Min Amount</th>
              <th class="px-6 py-3 font-semibold text-gray-700">Expires</th>
              <th class="px-6 py-3 font-semibold text-gray-700">Used</th>
              <th class="px-6 py-3 font-semibold text-gray-700">Status</th>
              <th class="px-6 py-3 font-semibold text-gray-700 text-right">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y">
            <tr v-for="coupon in coupons" :key="coupon.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 font-medium text-gray-900">{{ coupon.code }}</td>
              <td class="px-6 py-4">
                <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                  {{ coupon.discountPercent }}%
                </span>
              </td>
              <td class="px-6 py-4">${{ coupon.minAmount || "0" }}</td>
              <td class="px-6 py-4 text-sm text-gray-600">
                {{ formatDate(coupon.expiryDate) }}
                <span v-if="isExpired(coupon.expiryDate)" class="ml-2 text-red-600 font-semibold">(Expired)</span>
              </td>
              <td class="px-6 py-4 text-center text-gray-600">{{ coupon.usageCount || 0 }}</td>
              <td class="px-6 py-4">
                <span 
                  :class="[
                    'inline-flex items-center px-3 py-1 rounded-full text-xs font-medium',
                    coupon.active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  ]"
                >
                  {{ coupon.active ? "Active" : "Inactive" }}
                </span>
              </td>
              <td class="px-6 py-4 text-right space-x-2">
                <button
                  @click="editCoupon(coupon)"
                  class="text-blue-600 hover:text-blue-900 font-medium text-sm"
                >
                  Edit
                </button>
                <button
                  @click="deleteCoupon(coupon.id)"
                  class="text-red-600 hover:text-red-900 font-medium text-sm"
                >
                  Delete
                </button>
              </td>
            </tr>
            <tr v-if="!coupons.length">
              <td colspan="7" class="px-6 py-8 text-center text-gray-500">
                No coupons found. Create one to get started!
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Success Alert -->
    <transition
      enter-active-class="animate__animated animate__fadeIn"
      leave-active-class="animate__animated animate__fadeOut"
    >
      <div v-if="successMessage" class="fixed top-4 right-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg shadow-lg">
        {{ successMessage }}
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  data() {
    return {
      coupons: [],
      form: {
        code: "",
        discountPercent: 0,
        minAmount: 0,
        expiryDate: "",
        active: true,
        description: "",
      },
      editingId: null,
      errors: {},
      formError: "",
      isSubmitting: false,
      successMessage: "",
    };
  },
  methods: {
    async fetchCoupons() {
      try {
        const res = await this.$store.dispatch("getCoupons");
        this.coupons = res || [];
      } catch (error) {
        console.error("Error fetching coupons:", error);
      }
    },
    resetForm() {
      this.form = {
        code: "",
        discountPercent: 0,
        minAmount: 0,
        expiryDate: "",
        active: true,
        description: "",
      };
      this.editingId = null;
      this.errors = {};
      this.formError = "";
    },
    validateForm() {
      this.errors = {};
      this.formError = "";

      if (!this.form.code || this.form.code.trim() === "") {
        this.errors.code = "Coupon code is required";
      }

      if (this.form.discountPercent < 0 || this.form.discountPercent > 100) {
        this.errors.discountPercent = "Discount must be between 0-100";
      }

      if (this.form.minAmount < 0) {
        this.errors.minAmount = "Minimum amount cannot be negative";
      }

      if (!this.form.expiryDate) {
        this.errors.expiryDate = "Expiry date is required";
      }

      return Object.keys(this.errors).length === 0;
    },
    async saveCoupon() {
      if (!this.validateForm()) {
        return;
      }

      this.isSubmitting = true;

      try {
        // Convert datetime-local to ISO string
        const expiryDate = new Date(this.form.expiryDate).toISOString();

        const payload = {
          code: this.form.code.toUpperCase(),
          discountPercent: this.form.discountPercent,
          minAmount: this.form.minAmount || 0,
          expiryDate,
          active: this.form.active,
          description: this.form.description,
        };

        if (this.editingId) {
          // Update existing coupon
          await this.$store.dispatch("updateCoupon", {
            id: this.editingId,
            ...payload,
          });
          this.successMessage = "Coupon updated successfully!";
        } else {
          // Create new coupon
          await this.$store.dispatch("createCoupon", payload);
          this.successMessage = "Coupon created successfully!";
        }

        this.resetForm();
        await this.fetchCoupons();

        setTimeout(() => {
          this.successMessage = "";
        }, 3000);
      } catch (error) {
        this.formError = error.response?.data?.message || "Failed to save coupon";
        console.error("Error saving coupon:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
    editCoupon(coupon) {
      this.editingId = coupon.id;
      // Convert ISO date to datetime-local format
      const date = new Date(coupon.expiryDate);
      const dateStr = date.toISOString().slice(0, 16);

      this.form = {
        code: coupon.code,
        discountPercent: coupon.discountPercent,
        minAmount: coupon.minAmount || 0,
        expiryDate: dateStr,
        active: coupon.active,
        description: coupon.description || "",
      };

      window.scrollTo(0, 0);
    },
    async deleteCoupon(id) {
      if (confirm("Are you sure you want to delete this coupon? This action cannot be undone.")) {
        try {
          await this.$store.dispatch("deleteCoupon", id);
          this.successMessage = "Coupon deleted successfully!";
          await this.fetchCoupons();

          setTimeout(() => {
            this.successMessage = "";
          }, 3000);
        } catch (error) {
          alert("Failed to delete coupon: " + (error.response?.data?.message || error.message));
          console.error("Error deleting coupon:", error);
        }
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    isExpired(dateStr) {
      if (!dateStr) return false;
      return new Date(dateStr) < new Date();
    },
  },
  mounted() {
    this.fetchCoupons();
  },
};
</script>

